import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { publicApi } from '../utils/api';
import { useFirmSettings } from '../contexts/FirmSettingsContext';

const MISSING_TOKEN = 'This link is missing its token. Open the link in your setup email, or ask your firm to send a new one.';
const EXPIRED_TOKEN = 'This link has expired or is no longer valid. Ask your firm to send a new setup email.';

function isExpiredToken(err) {
  const msg = String(err?.message || '');
  const code = String(err?.code || '');
  if (err?.status === 401 || err?.status === 410) return true;
  if (/TOKEN|EXPIRED|INVALID/i.test(code)) return true;
  return /expir|invalid|already used|no longer valid/i.test(msg);
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const { firm } = useFirmSettings();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(token ? '' : MISSING_TOKEN);
  const [tokenDead, setTokenDead] = useState(!token);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!token) {
      setTokenDead(true);
      setError(MISSING_TOKEN);
      return;
    }
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await publicApi.completePasswordSetup(token, password);
      setDone(true);
    } catch (err) {
      if (isExpiredToken(err)) {
        setTokenDead(true);
        const msg = err.message && !String(err.message).startsWith('Request failed') ? err.message : EXPIRED_TOKEN;
        setError(msg);
      } else {
        setError(err.message || 'The password could not be saved. Try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <h1>{done ? 'Password set' : (firm.firm_name || 'Firm Portal')}</h1>
          <p>{done ? 'You can sign in with your new password.' : 'Choose a password'}</p>
        </div>

        {done ? (
          <>
            <Link className="btn btn-primary btn-block" to="/admin">Sign in</Link>
            <p style={{ marginTop: 16, fontSize: 13, textAlign: 'center' }}>
              <Link to="/login">Client portal sign in</Link>
            </p>
          </>
        ) : (
          <>
            {error && <div className="alert alert-error">{error}</div>}
            {tokenDead ? (
              <Link className="btn btn-primary btn-block" to="/admin">Back to sign in</Link>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Minimum 8 characters"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? <><span className="spinner" />Saving...</> : 'Set password'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
